// ===========================================
// Week Tile Picker Component
// ===========================================
// Reusable term-week selection component: admin reschedule / replacement /
// cancel / pre-booking flows, and the manual-invoice week selector.
// Renders one tile per term week (Wk N + its date range), mirroring the
// date-tile UX used elsewhere. Weeks that have fully passed are disabled
// unless the caller opts in via `allowPast` (see the prop docs).
//
// A tile is un-clickable for one of two INDEPENDENT reasons, which are tracked
// separately so they can be styled and explained differently:
//   - isPast     → the occurrence has already happened (relaxable via allowPast)
//   - noSession  → the lesson doesn't run that week (never relaxable — there is
//                  no occurrence to act on)
// Out-of-term weeks (a Monday-anchored week 1 can start before the term begins)
// count as noSession, NOT past — `allowPast` must never unlock them.
//
// WHICH "past" applies is chosen by `pastRule`, because SOURCE and DESTINATION
// pickers ask different questions:
//   - a SOURCE week identifies an existing occurrence → judge by that
//     occurrence's date ('occurrence', the default)
//   - a DESTINATION week is a container any day of which may be chosen → judge
//     by the week's last day ('week-end')
// Conflating the two locked a destination week the moment the lesson's own
// weekday passed, hiding still-bookable later days in that week (MNM, Aug 2026:
// a Sunday destination was unreachable from Saturday because the class ran on
// weekdays). Do NOT re-derive the rule from `occurrenceDayOfWeek`'s presence —
// destination callers legitimately pass it for `sessionWeeks`/labelling.

import { useMemo } from 'react';
import { Box, Grid, Paper, Tooltip, Typography } from '@mui/material';
import { addDays, format } from 'date-fns';
import {
  parseUTCDate,
  termWeekCount,
  termWeekAnchor,
  occurrenceDateForWeek,
  startOfTodayUTC,
} from '../../utils/dateUtils';

interface WeekTilePickerProps {
  /** Term start date (ISO string), used to derive week count + ranges */
  termStartDate: string;
  /** Term end date (ISO string), used to derive the last week number */
  termEndDate: string;
  /** Currently selected week number (1-indexed); 0/undefined = none. Single-select. */
  selectedWeek?: number;
  /** Callback when a week is selected. Single-select. */
  onWeekSelect?: (week: number) => void;
  /** The booking's current week, labelled "(current)" on its tile */
  currentWeek?: number;
  /**
   * Opt-in multi-select mode. When true, use `selectedWeeks` + `onToggleWeek`
   * instead of `selectedWeek` + `onWeekSelect`. Defaults to false so existing
   * single-select callers (1:1 clinic reschedule) are unchanged.
   */
  multiSelect?: boolean;
  /** Selected week numbers (1-indexed). Multi-select only. */
  selectedWeeks?: number[];
  /** Callback toggling a week's selection. Multi-select only. */
  onToggleWeek?: (week: number) => void;
  /**
   * The lesson's day-of-week (0=Sun..6=Sat). When provided, a week is treated as
   * "past" based on its actual LESSON-OCCURRENCE date (occurrenceDateForWeek —
   * this weekday within the Monday-anchored week N), matching the backend's
   * per-week rule — so a tile isn't selectable when the occurrence has already
   * happened, even if the week's Sun-end is still in the future. Omit to fall
   * back to the week-end rule.
   */
  occurrenceDayOfWeek?: number;
  /**
   * Optional whitelist of weeks the lesson actually RUNS a session (hybrid
   * group/individual/band weeks, or band rehearsal weeks). When provided, weeks
   * outside this set are disabled — they have no occurrence to act on, so e.g.
   * assigning a replacement there would create an exception that never renders.
   * Omit for lessons that run every term week (no restriction).
   */
  sessionWeeks?: number[];
  /**
   * Opt in to selecting weeks whose occurrence has already PASSED ("hindsight
   * admin" — e.g. a teacher was away and the class was never rescheduled at the
   * time, so the admin needs to move it after the fact).
   *
   * Relaxes ONLY the past-date rule. Weeks with no session (`sessionWeeks`) and
   * weeks whose occurrence falls outside the term stay disabled regardless —
   * there is no occurrence there to act on.
   *
   * Also used by the manual-invoice week selector (`LineItemWeekRow`), where a
   * past week is the NORMAL case: a mid-term joiner is invoiced after their first
   * weeks, and a term is often billed on its last day. The backend puts no date
   * bound on invoiced weeks.
   *
   * Defaults to false, so every existing caller is unchanged. The backend
   * already permits a past-occurrence reschedule inside a running term (the
   * ended-term 409 still applies), so this is purely a UI unlock.
   */
  allowPast?: boolean;
  /**
   * With `allowPast`, flag a selectable past week as unusual (dashed amber
   * border). Defaults to true — for a reschedule, acting on history is the
   * exception and should look it. Billing passes false: there every week is
   * usually in the past, and a grid of warnings warns about nothing.
   */
  highlightPast?: boolean;
  /**
   * Which question "has this week passed?" answers.
   *
   *  - 'occurrence' (default): judge by THIS lesson's occurrence date within
   *      the week. Correct for SOURCE pickers, where the week identifies an
   *      existing occurrence of this lesson. Requires `occurrenceDayOfWeek`;
   *      without it this falls back to the week-end rule.
   *
   *  - 'week-end': judge by the week's LAST day. Correct for DESTINATION
   *      pickers, where any day of the week may be chosen — such a week stays
   *      live until its final day has elapsed, even though the lesson's own
   *      weekday within it has passed.
   *
   * Defaults to 'occurrence' so every existing call site is unchanged.
   *
   * ⚠️ A 'week-end' caller MUST filter elapsed DAYS itself (see the callers'
   * slot filtering). Neither admin slot generator applies a past-date filter,
   * so relaxing the week rule without a day filter would offer already-passed
   * dates as bookable destinations.
   */
  pastRule?: 'occurrence' | 'week-end';
  /**
   * First term week the CLASS runs (`lessonWeekWindow(...).first`). A week before it
   * is disabled as `outsideWindow` ("Class starts in week N"). Omitted = no lower
   * bound, exactly the behaviour before mid-term start weeks existed.
   *
   * ⚠️ UNCONDITIONAL: `allowPast` (or any other relaxation) never unlocks it. A past
   * week the class ran is history you may act on; a week before the class existed
   * has nothing to act on, and billing it would be wrong.
   */
  windowFirst?: number;
  /**
   * Last term week the CLASS runs (`lessonWeekWindow(...).last`, inclusive). A week
   * after it is disabled as `outsideWindow` ("Class ends in week N"). Omitted = no
   * upper bound. Unconditional in the same way as `windowFirst`.
   */
  windowLast?: number;
}

interface WeekTile {
  week: number;
  start: Date;
  end: Date;
  /** The occurrence has already happened. Relaxable via `allowPast`. */
  isPast: boolean;
  /** No session this week (not in `sessionWeeks`, or outside the term). Never relaxable. */
  noSession: boolean;
  /** Before the class's first week / after its last (`windowFirst`/`windowLast`). Never relaxable. */
  outsideWindow: 'before' | 'after' | null;
  /** Resolved click/style gate: past-and-not-allowed, no session, or outside the class window. */
  disabled: boolean;
}

/**
 * WeekTilePicker - term-week selection as a tile grid.
 *
 * Used in admin clinic reschedule dialogs (CalendarEventDialog,
 * StudentBookingsList) so admins can move a 1:1 clinic into any week of the
 * term; in the class-level cancel / reschedule / replacement / room dialogs and
 * hybrid pre-booking; and, multi-select with `allowPast`, in the manual-invoice
 * week selector (LineItemWeekRow).
 *
 * Each tile's date range mirrors the backend's week buckets exactly: weeks
 * are Mon–Sun calendar weeks anchored to the Monday of the term-start week
 * (see utils/termWeeks.ts termWeekAnchor), clamped to the term's own dates —
 * so a mid-week-starting term shows a short week 1 rather than weeks that
 * straddle Monday.
 */
export default function WeekTilePicker({
  termStartDate,
  termEndDate,
  selectedWeek,
  onWeekSelect,
  currentWeek,
  multiSelect = false,
  selectedWeeks,
  onToggleWeek,
  occurrenceDayOfWeek,
  sessionWeeks,
  allowPast = false,
  highlightPast = true,
  pastRule = 'occurrence',
  windowFirst,
  windowLast,
}: WeekTilePickerProps) {
  const selectedSet = useMemo(() => new Set(selectedWeeks ?? []), [selectedWeeks]);
  const sessionSet = useMemo(() => (sessionWeeks ? new Set(sessionWeeks) : null), [sessionWeeks]);
  const tiles = useMemo<WeekTile[]>(() => {
    if (!termStartDate || !termEndDate) return [];
    const termStart = parseUTCDate(termStartDate);
    const termEnd = parseUTCDate(termEndDate);
    const lastWeek = termWeekCount(termStartDate, termEnd);
    // Compare against UTC midnight today so the boundary check matches the
    // UTC-parsed term dates (parseUTCDate); avoids local-tz off-by-one.
    const today = startOfTodayUTC();

    const anchor = termWeekAnchor(termStart);
    const result: WeekTile[] = [];
    for (let week = 1; week <= lastWeek; week++) {
      const gridStart = addDays(anchor, (week - 1) * 7);
      // Clamp week 1 to the term start (a mid-week term's first week is short)
      // and the final week to the term end.
      const start = gridStart < termStart ? termStart : gridStart;
      const rawEnd = addDays(gridStart, 6);
      const end = rawEnd > termEnd ? termEnd : rawEnd;
      // Default: the week-end boundary — the week is live until its last day
      // has elapsed. This is the correct rule for a DESTINATION week.
      let isPast = end < today;
      // An occurrence outside the term has nothing to act on — e.g. week 1's
      // Monday in a Tuesday-starting term (Monday-anchored weeks). That is NOT
      // "past": it must stay disabled even when allowPast is set.
      let outOfTerm = false;
      // Under the occurrence rule, judge "past" on the actual lesson OCCURRENCE
      // date (matching the backend per-week rule). Only meaningful when we know
      // the lesson's weekday, so fall back to the week-end rule without it.
      if (pastRule === 'occurrence' && occurrenceDayOfWeek != null) {
        const occurrence = occurrenceDateForWeek(termStart, week, occurrenceDayOfWeek);
        isPast = occurrence < today;
        // Deliberately scoped to the occurrence rule: a destination week is a
        // container, so "the lesson's weekday falls outside the term" says
        // nothing about whether the week can receive a session. Judging it here
        // labelled live destination weeks "No class this week".
        outOfTerm = occurrence < termStart || occurrence > termEnd;
      }
      // A week the lesson doesn't run is shown for context but not selectable.
      const noSession = (sessionSet ? !sessionSet.has(week) : false) || outOfTerm;
      // The class window is its own reason and is NOT touched by `allowPast`.
      const outsideWindow: WeekTile['outsideWindow'] =
        windowFirst != null && week < windowFirst
          ? 'before'
          : windowLast != null && week > windowLast
            ? 'after'
            : null;
      result.push({
        week,
        start,
        end,
        isPast,
        noSession,
        outsideWindow,
        disabled: (isPast && !allowPast) || noSession || outsideWindow !== null,
      });
    }
    return result;
  }, [
    termStartDate,
    termEndDate,
    occurrenceDayOfWeek,
    sessionSet,
    allowPast,
    pastRule,
    windowFirst,
    windowLast,
  ]);

  if (tiles.length === 0) return null;

  return (
    <Grid container spacing={1} sx={{ mb: 2 }}>
      {tiles.map(({ week, start, end, isPast, noSession, outsideWindow, disabled }) => {
        const isSelected = multiSelect ? selectedSet.has(week) : selectedWeek === week;
        const sameMonth = start.getMonth() === end.getMonth();
        const rangeLabel = sameMonth
          ? `${format(start, 'd')} – ${format(end, 'd MMM')}`
          : `${format(start, 'd MMM')} – ${format(end, 'd MMM')}`;
        // Distinguish the two disable reasons — they look identical otherwise,
        // which makes "why can't I click this?" undiagnosable.
        // The window reason wins: "Class starts in week 5" explains a pre-start week
        // better than "No class this week", which a session-week whitelist also sets.
        const disabledReason = outsideWindow
          ? outsideWindow === 'before'
            ? `Class starts in Week ${windowFirst}`
            : `Class ends in Week ${windowLast}`
          : noSession
            ? 'No class this week'
            : disabled
              ? 'This week has already passed'
              : '';
        // A selectable past week (allowPast) is flagged rather than greyed, so
        // the admin can see they're acting on history.
        // Suppressible via `highlightPast` where past weeks are routine (billing).
        const isSelectablePast = isPast && !disabled && highlightPast;
        return (
          // 2 per row on phones, 3 on small, 4 on wider dialogs — equal-width
          // cells keep the tiles in aligned columns rather than ragged wrapping.
          <Grid item xs={6} sm={4} md={3} key={week}>
            <Tooltip
              title={disabledReason}
              placement="top"
              arrow
              disableHoverListener={!disabledReason}
            >
              {/* span wrapper: MUI Tooltip needs a DOM child that fires events
                  even when the tile is non-interactive. */}
              <Box component="span" sx={{ display: 'block', height: '100%' }}>
                <Paper
                  variant="outlined"
                  aria-disabled={disabled || undefined}
                  onClick={() => {
                    if (disabled) return;
                    if (multiSelect) onToggleWeek?.(week);
                    else onWeekSelect?.(week);
                  }}
                  sx={{
                    py: 1,
                    px: 1,
                    height: '100%',
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    borderColor: isSelected
                      ? 'primary.main'
                      : isSelectablePast
                        ? 'warning.main'
                        : 'divider',
                    // Dashed border marks a past-but-selectable week as unusual.
                    borderStyle: isSelectablePast && !isSelected ? 'dashed' : 'solid',
                    borderWidth: isSelected ? 2 : 1,
                    bgcolor: isSelected
                      ? 'primary.main'
                      : disabled
                        ? 'action.disabledBackground'
                        : 'background.paper',
                    color: isSelected
                      ? 'primary.contrastText'
                      : disabled
                        ? 'text.disabled'
                        : 'text.primary',
                    // No-session weeks fade further than merely-past ones: they are
                    // pure context, whereas a past week may become actionable.
                    opacity: noSession || outsideWindow ? 0.4 : disabled ? 0.5 : 1,
                    transition: 'all 0.15s',
                    '&:hover': disabled
                      ? {}
                      : {
                          borderColor: 'primary.main',
                          bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                        },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Wk {week}
                    {week === currentWeek ? ' •' : ''}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                    {rangeLabel}
                  </Typography>
                </Paper>
              </Box>
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );
}
